/**
 * PlanCheckLetterModal — make the plan check submittal cover letter for one agency review
 * (docs/PLAN_CHECK_PERFORMANCE.md). Opens via CustomEvent('open-plan-check-letter',
 * { detail: { reviewId, reviewName } }) from the agency review's ⋯ menu, or from the Log Activity
 * modal when a submittal is being logged.
 *
 * Prefills from the review (agency, contact, project, address, permit, tracking number) and from
 * the signed-in team member; every line stays editable because the letter goes out under their
 * name. The letter's paragraphs live server-side in api/plan-check-letter.js — one place.
 */
import { useEffect, useState } from 'react'
import { ctx } from '../outline/ctx.js'
import { getPlanCheckLetterPrefill, renderPlanCheckLetter } from '../services/planCheckLetter.js'

const PREFS = 'aj:plancheck:letter'
const longDate = () => new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
function readPrefs() { try { return JSON.parse(localStorage.getItem(PREFS) || '{}') } catch { return {} } }

const EMPTY = {
  date: '', contactName: '', agency: '', salutation: '', projectName: '', address: '', permitType: '',
  trackingNumber: '', projectNumber: '', enclosures: '', commit: true, commitDays: 10,
  signerName: '', signerTitle: '', signerEmail: '', signerPhone: '', cc: '',
}

export default function PlanCheckLetterModal() {
  const [open, setOpen] = useState(false)
  const [reviewName, setReviewName] = useState('')
  const [L, setL] = useState(EMPTY)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const set = (k, v) => setL(s => ({ ...s, [k]: v }))

  useEffect(() => {
    function onOpen(e) {
      const d = (e && e.detail) || {}
      if (!d.reviewId) return
      const prefs = readPrefs()
      const m = (ctx.currentMember && ctx.currentMember.fields) || {}
      setReviewName(d.reviewName || 'Agency review')
      setL({
        ...EMPTY,
        date: longDate(),
        commit: prefs.commit !== false,
        commitDays: prefs.commitDays || 10,
        signerName: m['Member Name'] || m['Name'] || '',
        signerTitle: prefs.signerTitle || '',
        signerEmail: m['Member Email'] || '',
        signerPhone: m['Member Phone Number'] || '',
      })
      setError(''); setBusy(false); setLoading(true); setOpen(true)
      getPlanCheckLetterPrefill(d.reviewId)
        .then(p => setL(s => ({ ...s, ...p })))
        .catch(err => setError(err.message))
        .finally(() => setLoading(false))
    }
    window.addEventListener('open-plan-check-letter', onOpen)
    return () => window.removeEventListener('open-plan-check-letter', onOpen)
  }, [])

  useEffect(() => {
    if (!open) return
    function onKey(e) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  async function make() {
    setBusy(true); setError('')
    try { localStorage.setItem(PREFS, JSON.stringify({ commit: L.commit, commitDays: L.commitDays, signerTitle: L.signerTitle })) } catch { /* ignore */ }
    try {
      const url = await renderPlanCheckLetter({ ...L, commitDays: L.commit ? Number(L.commitDays) || null : null })
      window.open(url, '_blank', 'noopener')
      setOpen(false)
    } catch (err) { setError(err.message) }
    setBusy(false)
  }

  if (!open) return null
  const S = STYLES
  const field = (k, label, opts = {}) => (
    <label style={{ ...S.field, ...(opts.wide ? { gridColumn: '1 / -1' } : null) }}>
      <span style={S.label}>{label}</span>
      {opts.multiline
        ? <textarea style={{ ...S.input, minHeight: 64, resize: 'vertical' }} value={L[k]} placeholder={opts.placeholder || ''} onChange={e => set(k, e.target.value)} />
        : <input style={S.input} value={L[k]} placeholder={opts.placeholder || ''} onChange={e => set(k, e.target.value)} />}
    </label>
  )
  return (
    <div style={S.backdrop} onMouseDown={e => { if (e.target === e.currentTarget) setOpen(false) }}>
      <div data-modal-id="PlanCheckLetterModal" style={S.box}>
        <div style={S.title}>Plan check cover letter</div>
        <div style={S.sub}>{reviewName}{loading ? ' · reading the review…' : ''}</div>
        <div style={S.intro}>
          Goes in the submittal package. It asks the department for the date the application is deemed complete
          and its estimated review time, and puts the owner's right to a licensed private plan checker after
          30 business days on record (Health and Safety Code 17960.3), in a cooperative tone.
        </div>

        <div style={S.group}>To</div>
        <div style={S.grid}>
          {field('contactName', 'Contact', { placeholder: 'Plan check staff' })}
          {field('agency', 'Agency')}
          {field('salutation', 'Salutation', { placeholder: L.contactName ? 'Dear ' + L.contactName + ':' : 'Dear Plan Check Staff:', wide: true })}
        </div>

        <div style={S.group}>Project</div>
        <div style={S.grid}>
          {field('projectName', 'Project')}
          {field('address', 'Address')}
          {field('permitType', 'Permit')}
          {field('trackingNumber', 'Agency no.', { placeholder: 'if assigned' })}
          {field('projectNumber', 'Our project no.')}
          {field('date', 'Date')}
          {field('enclosures', 'What the submittal includes (optional, one per line)', { multiline: true, wide: true, placeholder: 'Architectural drawings, 42 sheets\nStructural calculations\nTitle 24 energy compliance' })}
        </div>

        <label style={S.commit}>
          <input type="checkbox" checked={L.commit} onChange={e => set('commit', e.target.checked)} />
          <span>Commit to returning corrections within</span>
          <input style={{ ...S.input, width: 44, textAlign: 'center', padding: '3px 4px' }} value={L.commitDays} disabled={!L.commit} onChange={e => set('commitDays', e.target.value.replace(/\D/g, ''))} />
          <span>business days</span>
        </label>

        <div style={S.group}>Signed by</div>
        <div style={S.grid}>
          {field('signerName', 'Name')}
          {field('signerTitle', 'Title', { placeholder: 'Permitting Coordinator' })}
          {field('signerEmail', 'Email')}
          {field('signerPhone', 'Phone')}
          {field('cc', 'cc (optional)', { wide: true, placeholder: 'Owner' })}
        </div>
        <div style={S.note}>Your saved signature (My Signature, in the menu) is added above your name.</div>

        {error && <div style={S.error}>{error}</div>}
        <div style={S.actions}>
          <button style={S.btn} onClick={() => setOpen(false)}>Cancel</button>
          <button style={S.primary} disabled={busy || loading} onClick={make}>{busy ? 'Making…' : 'Make letter'}</button>
        </div>
      </div>
    </div>
  )
}

const ff = '"DM Sans",sans-serif'
const STYLES = {
  backdrop: { position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  box: { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 560, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto', fontFamily: ff, color: 'var(--aj-text)' },
  title: { fontSize: 13, fontWeight: 600 },
  sub: { fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 },
  intro: { fontSize: 12, color: 'var(--aj-text-mid)', lineHeight: 1.5, margin: '12px 0 4px' },
  group: { fontSize: 11, fontWeight: 600, color: 'var(--aj-text-mid)', margin: '14px 0 6px' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  field: { display: 'flex', flexDirection: 'column', gap: 3 },
  label: { fontSize: 11, color: 'var(--aj-text-muted)' },
  input: { fontFamily: ff, fontSize: 12, padding: '6px 8px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', boxSizing: 'border-box', width: '100%' },
  commit: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 14 },
  note: { fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 6 },
  error: { color: '#c0392b', fontSize: 12, marginTop: 10 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
  btn: { fontFamily: ff, fontSize: 12, padding: '6px 14px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', cursor: 'pointer' },
  primary: { fontFamily: ff, fontSize: 12, padding: '6px 14px', borderRadius: 4, border: 'none', background: '#07272D', color: '#fff', cursor: 'pointer' },
}
